import { Dispatch, FC, MouseEvent, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Menu, MenuItem, Stack } from '@mui/material';

import { AttachmentVersion } from '@/api/data-contracts';
import { ReactComponent as MoreIcon } from '@/assets/icons/MoreHorizRounded.svg';
import {
  Container,
  MenuButton,
  Thumbnail,
  Title,
  VersionTag,
} from '@/features/media/ui/FileVersions/Version.styles';
import useFiles from '@/shared/api/files.service';
import ConfirmationDialog from '@/shared/ui/ConfirmationDialog';

interface Props {
  file: File;
  setActiveFile: Dispatch<SetStateAction<File | undefined>>;
  version: AttachmentVersion;
  versionNumber: number;
  isActiveVersion?: boolean;
  setActiveVersion?: (versionId: number) => void;
  deleteVersion?: (versionId: number) => void;
  disableVersionUpload?: boolean;
}

const Version: FC<Props> = ({
  file,
  setActiveFile,
  version,
  versionNumber,
  isActiveVersion,
  setActiveVersion,
  deleteVersion,
  disableVersionUpload,
}) => {
  const { t } = useTranslation();
  const { downloadThumbnail } = useFiles();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showConfirmationDialog, setshowConfirmationDialog] = useState<boolean>(false);

  const [thumbnail, setThumbnail] = useState<File | undefined>();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setshowConfirmationDialog(false);
    setAnchorEl(null);
  };

  const renderConfirmationDialog = useCallback(
    () => (
      <ConfirmationDialog
        open={showConfirmationDialog}
        handleClose={() => {
          handleClose();
        }}
        handleConfirm={() => {
          deleteVersion?.(version.id);
          handleClose();
        }}
        title={t('Delete version')}
        description={t('Are you sure you want to delete this version?')}
      />
    ),
    [deleteVersion, showConfirmationDialog, t, version.id],
  );

  useEffect(() => {
    if (version) {
      downloadThumbnail(version.filePointer.objectId!).then((blob) =>
        setThumbnail(new File([blob], version.filePointer.name as string)),
      );
    }
  }, [downloadThumbnail, version]);

  return file ? (
    <>
      <Container onClick={() => setActiveFile(file)}>
        <Thumbnail src={thumbnail && URL.createObjectURL(thumbnail)} />

        <Stack sx={{ width: '100%' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Title>{version.filePointer.name}</Title>
            {!isActiveVersion && !disableVersionUpload && (
              <MenuButton onClick={handleClick} size={'small'}>
                <MoreIcon />
              </MenuButton>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" justifyContent="flex-end">
            <VersionTag current={isActiveVersion}>
              <p>V{versionNumber}</p>
            </VersionTag>
          </Stack>
        </Stack>
      </Container>

      {!disableVersionUpload && (
        <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
          <MenuItem
            onClick={() => {
              setActiveVersion?.(version.id);
              setActiveFile(file);
              handleClose();
            }}
          >
            {t('Set current version')}
          </MenuItem>
          <MenuItem onClick={() => setshowConfirmationDialog(true)}>{t('Delete version')}</MenuItem>
        </Menu>
      )}

      {renderConfirmationDialog()}
    </>
  ) : null;
};

export default Version;
